// src/components/AppShell.tsx
//
// Persistent app shell that wraps every authenticated page: a slim top bar
// (logo + company + top-right UserMenu with the tenant switcher), the global
// AppSidebar (module -> sub-tab nav that replaces the old landing tiles), the
// page content in a scrollable main area, and the floating Assistant button.
//
// Pages keep their own in-page headers/sub-nav for now; the shell adds the
// persistent chrome around them.

import type { ReactNode } from 'react';
import type { Page } from '../App';
import { useAuthContext } from '../hooks/useAuth';
import AppSidebar from './AppSidebar';
import UserMenu from './UserMenu';
import AssistantLauncher from './AssistantLauncher';

interface AppShellProps {
  currentPage: Page;
  onNavigate: (page: Page) => void;
  children: ReactNode;
}

export default function AppShell({ currentPage, onNavigate, children }: AppShellProps) {
  const { branding } = useAuthContext();

  return (
    <div className="h-screen flex flex-col bg-slate-950">
      {/* Top bar */}
      <header className="shrink-0 h-14 flex items-center justify-between px-3 sm:px-4 border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-md z-40">
        <button
          onClick={() => onNavigate('landing')}
          className="flex items-center gap-2.5 min-w-0"
          title="Home"
        >
          <div className="w-8 h-8 shrink-0 rounded-lg bg-white/5 ring-1 ring-slate-700/40 flex items-center justify-center p-1">
            <img
              src={branding?.logo_url || '/delldata_logo.png'}
              alt={branding?.company_name || 'GeniReports'}
              className="w-full h-full object-contain"
            />
          </div>
          <span className="text-sm font-bold text-white tracking-tight truncate hidden sm:block">
            {branding?.company_name || 'GeniReports'}
          </span>
        </button>

        <UserMenu />
      </header>

      {/* Sidebar + content */}
      <div className="flex flex-1 min-h-0">
        <AppSidebar currentPage={currentPage} onNavigate={onNavigate} />
        <main className="flex-1 min-w-0 overflow-y-auto">{children}</main>
      </div>

      <AssistantLauncher />
    </div>
  );
}
